import React, { useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { IncidentGroup } from '../../services/incidentsApi';
import type { IncidentGroupPatternActionMode } from './IncidentGroupPatternActionDialog';
import {
  DEEP_REVIEW_AUDIT_LABEL,
  RISK_DECISION_LABEL,
  RISK_REVIEW_LABEL,
  REPRESENTATIVE_AUDIT_LABEL,
  REPRESENTATIVE_AUDIT_SUBTITLE,
  REPRESENTATIVE_INCIDENT_LABEL,
  buildGroupedEvidenceStatusChipLabel,
  formatPatternVolumeCount,
  RISK_ACTION_SCOPE_BASELINE,
  patternActionScopeDisplayLine,
  patternDecisionActionScopePendingLine,
  patternVolumeActionScopeWarningCopy,
  PATTERN_VOLUME_SIGNALS_DISCLAIMER,
  patternVolumeDisplayLine,
  ACTION_OWNER_ASSIGN_STEP_HINT,
  ACTION_OWNER_ROLE_HELPER,
  RISK_OWNER_ROLE_HELPER,
  assignRiskOwnerActionLabel,
  buildRiskDecisionRecommendedNextStep,
  ownerRoleIsUnassigned,
} from '../../domain/incidents/incidentPatternVocabulary';
import type { ReviewWindowContext } from '../../domain/incidents/reviewWindowContext';
import {
  formatObservationBadge,
  formatReviewWindowHeaderCaption,
  formatReviewWindowHeaderValue,
} from '../../domain/incidents/reviewWindowContext';
import { AfterSignedScanTimelineSelector } from './AfterSignedScanTimelineSelector';
import type { RiskPatternWorkflowReviewTimelineProps } from './RiskPatternWorkflowReviewContextPanel';
import {
  displayPatternEvidenceProofLabel,
  displayPatternWorkflowLabel,
} from '../../domain/incidents/patternReviewDisplayLabels';
import type { PatternVolumeKind } from '../../domain/incidents/patternReviewScopeSemantics';
import {
  type AuditScopeBadgeLabel,
  representativeAuditScopeBanner,
} from '../../domain/incidents/groupedRepresentativeAuditCopy';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import {
  buildPatternReviewWorkflowProgress,
  GROUPED_SAFETY_REPORT_WORKFLOW_STEPS,
} from '../../domain/safetyWorkflowStepMapping';
import SafetyWorkflowStepper from '../safety/SafetyWorkflowStepper';
import { formatHotspotOperationalSummary, PATTERN_EVALUATION_WINDOW_LABEL } from '../../domain/incidents/observationDispositionCopy';

export const AuditScopeBadge: React.FC<{ label: AuditScopeBadgeLabel }> = ({ label }) => (
  <Chip
    size="small"
    label={label}
    variant="outlined"
    sx={{ ml: 1, height: 20, fontSize: 11, fontWeight: 700, verticalAlign: 'middle' }}
  />
);

export type PatternScopeSummaryProps = {
  patternVolumeCount: number;
  patternVolumeKind?: PatternVolumeKind;
  actionScopeMatchedCount?: number | null;
  actionScopePending?: boolean;
  showScopeWarning?: boolean;
};

/** Read-side volume vs write-side action scope (dry-run). */
export const PatternScopeSummary: React.FC<PatternScopeSummaryProps> = ({
  patternVolumeCount,
  patternVolumeKind = 'review_window',
  actionScopeMatchedCount = null,
  actionScopePending = true,
  showScopeWarning = true,
}) => {
  const volume = Math.max(0, Number(patternVolumeCount) || 0);
  const scopeKnown = actionScopeMatchedCount != null && !actionScopePending;
  const scope = scopeKnown ? Math.max(0, Number(actionScopeMatchedCount) || 0) : null;
  const differs = showScopeWarning && scopeKnown && scope != null && volume !== scope && volume > 1;
  const warning = differs && scope != null
    ? patternVolumeActionScopeWarningCopy(volume, scope)
    : null;

  return (
    <Box sx={{ mt: 1.25 }} data-testid="pattern-scope-summary">
      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.55 }}>
        {patternVolumeDisplayLine(volume, patternVolumeKind)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.55, mt: 0.35 }}>
        {PATTERN_VOLUME_SIGNALS_DISCLAIMER}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.55, mt: 0.35 }}>
        {scopeKnown
          ? patternActionScopeDisplayLine(scope)
          : patternDecisionActionScopePendingLine()}
      </Typography>
      {warning ? (
        <Alert severity="warning" sx={{ mt: 1.25, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {warning.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.55 }}>
            {warning.body}
            <br />
            <br />
            {warning.footer}
          </Typography>
        </Alert>
      ) : null}
    </Box>
  );
};

export type GroupedReviewStatusRowProps = {
  patternVolumeCount: number;
  patternVolumeKind?: PatternVolumeKind;
  reviewWindowContext: ReviewWindowContext;
  recurrenceLabel?: string;
  workflowLabel: string;
  onViewRelatedIncidents?: () => void;
  viewRelatedLabel?: string;
};

/** Primary status row for grouped full-page review (occurrence count first). */
export const GroupedReviewStatusRow: React.FC<GroupedReviewStatusRowProps> = ({
  patternVolumeCount,
  patternVolumeKind: _patternVolumeKind = 'review_window',
  reviewWindowContext,
  recurrenceLabel,
  workflowLabel,
  onViewRelatedIncidents,
  viewRelatedLabel = 'View pattern observations',
}) => (
  <Stack
    direction="row"
    spacing={1}
    useFlexGap
    flexWrap="wrap"
    alignItems="center"
    sx={{ mt: 1 }}
    data-testid="grouped-review-status-row"
  >
    <Chip
      label={formatObservationBadge(Math.max(0, patternVolumeCount), reviewWindowContext)}
      color="primary"
      sx={{ fontWeight: 900, fontSize: 13, height: 30, '& .MuiChip-label': { px: 1.25 } }}
      title={formatObservationBadge(Math.max(0, patternVolumeCount), reviewWindowContext)}
    />
    {recurrenceLabel ? (
      <Chip
        label={recurrenceLabel}
        color="warning"
        variant="outlined"
        sx={{ fontWeight: 800, fontSize: 12.5 }}
      />
    ) : null}
    <Chip
      label={workflowLabel || 'Needs review'}
      color="warning"
      variant="outlined"
      sx={{ fontWeight: 800, fontSize: 12.5 }}
    />
    {onViewRelatedIncidents ? (
      <Button
        variant="text"
        size="small"
        onClick={onViewRelatedIncidents}
        sx={{ px: 0, minWidth: 0, fontWeight: 800, textTransform: 'none' }}
      >
        {viewRelatedLabel}
      </Button>
    ) : null}
  </Stack>
);

export type PatternReviewHeaderProps = {
  title: string;
  workflowLabel: string;
  evidenceStatusLabel: string;
  patternVolumeCount: number;
  patternVolumeKind?: PatternVolumeKind;
  representativeIncidentId?: string;
  currentIncidentId?: string;
  recurrenceLabel?: string;
  firstSeenLabel?: string;
  lastSeenLabel?: string;
  seriesSummaryText?: string;
  onViewRelatedIncidents?: () => void;
};

export type PersistentRiskSummaryHeaderProps = PatternReviewHeaderProps & {
  patternOwner: string;
  followUpOwner: string;
  nextStepLabel: string;
  whatToKnow?: string;
};

/** Persistent top summary for grouped recurring-risk review (workflow-first layout). */
export const PersistentRiskSummaryHeader: React.FC<PersistentRiskSummaryHeaderProps> = ({
  patternOwner,
  followUpOwner,
  nextStepLabel,
  whatToKnow,
  ...headerProps
}) => {
  const normalizedPatternOwner = ownerRoleIsUnassigned(patternOwner) ? 'Unassigned' : patternOwner;
  const normalizedFollowUpOwner = ownerRoleIsUnassigned(followUpOwner) ? 'Unassigned' : followUpOwner;

  return (
  <Box data-testid="persistent-risk-summary-header">
    <PatternReviewHeader {...headerProps} />
    {whatToKnow ? (
      <Typography variant="body2" sx={{ fontWeight: 700, mt: -1, mb: 1.5, lineHeight: 1.55 }}>
        {whatToKnow}
      </Typography>
    ) : null}
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        mb: 2,
        borderRadius: 3,
        borderColor: '#d0d7e2',
        bgcolor: '#ffffff',
      }}
    >
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Status</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {displayPatternWorkflowLabel(headerProps.workflowLabel) || 'Needs review'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Pattern observations</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {formatPatternVolumeCount(headerProps.patternVolumeCount, headerProps.patternVolumeKind)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
            {PATTERN_VOLUME_SIGNALS_DISCLAIMER}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">{displaySafetyManagerLabel('pattern_owner')}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>{normalizedPatternOwner}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">{displaySafetyManagerLabel('followup_owner')}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }} data-testid="persistent-summary-action-owner">
            {normalizedFollowUpOwner}
          </Typography>
          {ownerRoleIsUnassigned(followUpOwner) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
              {ACTION_OWNER_ASSIGN_STEP_HINT}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Evidence</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {displayPatternEvidenceProofLabel(headerProps.evidenceStatusLabel) || 'Not evaluated'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <Typography variant="caption" color="text.secondary">Next step</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }} data-testid="persistent-risk-next-step">
            {nextStepLabel}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  </Box>
  );
};

export type GroupedIncidentHeatmapSectionProps = {
  heatmapSummaryText?: string;
  heatmapLoading: boolean;
  heatmapError: string | null;
  heatmapPageFeedback?: string | null;
  heatmapCard: React.ReactNode;
  sectionTitle?: string;
  sectionHelper?: string;
  showCellNavigationHint?: boolean;
};

export const GroupedIncidentHeatmapSection: React.FC<GroupedIncidentHeatmapSectionProps> = ({
  heatmapLoading,
  heatmapError,
  heatmapPageFeedback,
  heatmapCard,
}) => (
  <Box data-testid="grouped-incident-heatmap">
    {heatmapPageFeedback ? (
      <Alert severity="info" sx={{ mb: 1.2 }} onClose={undefined}>
        {heatmapPageFeedback}
      </Alert>
    ) : null}
    {heatmapLoading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">Loading recurring pattern by hour…</Typography>
      </Box>
    ) : heatmapError ? (
      <Alert severity="warning">{heatmapError}</Alert>
    ) : (
      heatmapCard
    )}
  </Box>
);

export type GroupedRiskPatternSummaryProps = {
  riskPattern: string;
  frequencyLine: string;
  locationLine: string;
  /** @deprecated Workflow accordion owns next-step guidance. */
  recommendedAction?: string;
  statusLine: string;
  /** Compact policy context that used to live in Advanced details. */
  contextLine?: string;
};

export const GroupedRiskPatternSummary: React.FC<GroupedRiskPatternSummaryProps> = ({
  riskPattern,
  frequencyLine,
  locationLine,
  statusLine,
  contextLine,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 3,
      borderColor: '#d0d7e2',
      bgcolor: '#ffffff',
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.5 }}>
      {riskPattern}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {frequencyLine} · {locationLine || 'Location not reported'} · {statusLine}
    </Typography>
    {contextLine ? (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
        {contextLine}
      </Typography>
    ) : null}
  </Paper>
);

export const PatternReviewHeader: React.FC<PatternReviewHeaderProps> = ({
  title,
  workflowLabel,
  evidenceStatusLabel,
  patternVolumeCount,
  patternVolumeKind = 'review_window',
  representativeIncidentId,
  currentIncidentId,
  recurrenceLabel,
  firstSeenLabel,
  lastSeenLabel,
  seriesSummaryText,
  onViewRelatedIncidents,
}) => {
  const activeIncidentId = String(currentIncidentId || '').trim();
  const representativeId = String(representativeIncidentId || '').trim();
  const viewingAlternateOccurrence = Boolean(
    activeIncidentId && representativeId && activeIncidentId !== representativeId,
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        mb: 2,
        borderRadius: 3,
        borderColor: '#d0d7e2',
        bgcolor: '#fbfcfe',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
        {RISK_REVIEW_LABEL}
      </Typography>
      <Typography sx={{ fontSize: 11, letterSpacing: 0.35, color: 'text.secondary', fontWeight: 700, mt: 0.35 }}>
        Risk pattern
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 900, lineHeight: 1.1, mt: 0.4 }}>
        {title}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
        <Chip
          label={formatPatternVolumeCount(patternVolumeCount, patternVolumeKind)}
          color="primary"
          sx={{ fontWeight: 800 }}
          title={patternVolumeDisplayLine(patternVolumeCount, patternVolumeKind)}
        />
        {recurrenceLabel ? <Chip label={recurrenceLabel} color="warning" variant="outlined" sx={{ fontWeight: 700 }} /> : null}
        <Chip
          label={displayPatternWorkflowLabel(workflowLabel) || 'Needs review'}
          color="warning"
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
        <Chip
          label={`Evidence/proof: ${displayPatternEvidenceProofLabel(evidenceStatusLabel) || 'Not evaluated'}`}
          variant="outlined"
        />
      </Stack>
      {viewingAlternateOccurrence ? (
        <Alert severity="info" sx={{ mt: 1.25, mb: 0.25, borderRadius: 2 }}>
          Viewing observation `{activeIncidentId}` from the selected heatmap hour. The risk summary below still refers to representative evidence `{representativeId}`.
        </Alert>
      ) : null}
      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={viewingAlternateOccurrence ? 3 : 4}>
          <Typography variant="caption" color="text.secondary">{REPRESENTATIVE_INCIDENT_LABEL}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {representativeId || 'Not reported'}
          </Typography>
        </Grid>
        {viewingAlternateOccurrence ? (
          <Grid item xs={12} md={3}>
            <Typography variant="caption" color="text.secondary">Currently viewing occurrence</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {activeIncidentId}
            </Typography>
          </Grid>
        ) : null}
        <Grid item xs={12} md={viewingAlternateOccurrence ? 3 : 4}>
          <Typography variant="caption" color="text.secondary">First seen</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {firstSeenLabel || 'Not reported'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={viewingAlternateOccurrence ? 3 : 4}>
          <Typography variant="caption" color="text.secondary">Last seen</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {lastSeenLabel || 'Not reported'}
          </Typography>
        </Grid>
      </Grid>
      {seriesSummaryText ? (
        <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {seriesSummaryText}
          </Typography>
          {onViewRelatedIncidents ? (
            <Button variant="text" size="small" onClick={onViewRelatedIncidents} sx={{ px: 0 }}>
              View pattern observations
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Paper>
  );
};

export type PatternDecisionCardProps = {
  group: IncidentGroup | null;
  patternVolumeCount: number;
  patternVolumeKind?: PatternVolumeKind;
  patternVolumeDisplay?: string;
  patternVolumeBadge?: string;
  reviewWindowContext?: ReviewWindowContext;
  currentPatternOwner?: string;
  patternAssignedBy?: string;
  followUpOwnerLabel?: string;
  workflowLabel?: string;
  groupingBasis: string;
  scopeSummary: string;
  policyLabel: string;
  recurrenceLabel: string;
  summaryText: string;
  actionScopeBaseline?: string;
  actionScopeLine?: string;
  canManagePatternActions: boolean;
  groupPatternActionBusy: boolean;
  actionAvailabilityMessage?: string;
  onGroupPatternAction?: (group: IncidentGroup, mode: IncidentGroupPatternActionMode) => void;
  /** Shortcut only — primary assignment lives in the Corrective action panel. */
  onOpenAssignAction?: () => void;
  /** Slim layout for Understand risk — no volume/scope duplication. */
  variant?: 'full' | 'understand_risk';
};

export const PatternDecisionCard: React.FC<PatternDecisionCardProps> = ({
  group,
  patternVolumeCount,
  patternVolumeKind: _patternVolumeKind = 'review_window',
  patternVolumeDisplay,
  patternVolumeBadge,
  reviewWindowContext,
  currentPatternOwner,
  patternAssignedBy,
  followUpOwnerLabel,
  workflowLabel: _workflowLabel,
  groupingBasis,
  scopeSummary,
  policyLabel,
  recurrenceLabel,
  summaryText,
  actionScopeBaseline = RISK_ACTION_SCOPE_BASELINE,
  actionScopeLine,
  canManagePatternActions,
  groupPatternActionBusy,
  actionAvailabilityMessage,
  onGroupPatternAction,
  onOpenAssignAction,
  variant = 'full',
}) => {
  void _workflowLabel;
  const understandRiskVariant = variant === 'understand_risk';
  const [moreActionsAnchor, setMoreActionsAnchor] = useState<null | HTMLElement>(null);
  const actionGuard = actionAvailabilityMessage || '';
  const actionDisabled = Boolean(actionGuard) || !group || !canManagePatternActions || !onGroupPatternAction;
  const volumeLine = patternVolumeBadge
    || patternVolumeDisplay
    || (reviewWindowContext
      ? formatObservationBadge(patternVolumeCount, reviewWindowContext)
      : `${patternVolumeCount.toLocaleString()} observations in selected review window`);
  const scopeLine = actionScopeLine || patternActionScopeDisplayLine(null, { pending: true });
  const recommendedNextStep = buildRiskDecisionRecommendedNextStep({
    patternOwnerLabel: currentPatternOwner,
    followUpOwnerLabel,
  });
  const riskOwnerLabel = displaySafetyManagerLabel('pattern_owner');
  const assignRiskOwnerLabel = assignRiskOwnerActionLabel(currentPatternOwner);
  const actionOwnerUnassigned = ownerRoleIsUnassigned(followUpOwnerLabel);
  const actionOwnerStatusLabel = actionOwnerUnassigned ? 'Unassigned' : String(followUpOwnerLabel || '').trim();
  const normalizedPatternOwner = ownerRoleIsUnassigned(currentPatternOwner) ? 'Unassigned' : currentPatternOwner;

  const triggerAction = (mode: IncidentGroupPatternActionMode) => {
    if (!group || actionDisabled || !onGroupPatternAction) return;
    onGroupPatternAction(group, mode);
  };

  const closeMoreActions = () => setMoreActionsAnchor(null);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        mb: 2,
        borderRadius: 3,
        borderColor: '#d0d7e2',
        bgcolor: '#fbfcfe',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      {!understandRiskVariant ? (
        <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
          {RISK_DECISION_LABEL}
        </Typography>
      ) : null}
      <Typography sx={{ fontSize: 18, fontWeight: 900, mt: 0.35, mb: 0.85 }}>
        {understandRiskVariant
          ? 'Manage this pattern'
          : 'Decide once for the recurring risk pattern, not one observation at a time'}
      </Typography>
      {!understandRiskVariant ? (
        <>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 1.1 }}>
        {summaryText}
      </Typography>
      <Alert severity="info" sx={{ mb: 1.25, borderRadius: 2 }} data-testid="risk-decision-recommended-next-step">
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.5 }}>
          Recommended next step: {recommendedNextStep}
        </Typography>
      </Alert>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35 }}>
        {volumeLine}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.55 }}>
        {scopeLine}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {actionScopeBaseline}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.55 }}>
        Assign and close apply to linked observations in the selected window only. Suppress pauses similar future observations and is available under More actions.
      </Typography>
      <Box sx={{ mb: 1.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {riskOwnerLabel}: {normalizedPatternOwner}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {RISK_OWNER_ROLE_HELPER}
        </Typography>
        {patternAssignedBy ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.55 }}>
            Risk owner set by {patternAssignedBy}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.75 }} data-testid="risk-decision-action-owner-status">
          {displaySafetyManagerLabel('followup_owner')}: {actionOwnerStatusLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }} data-testid="risk-decision-action-owner-hint">
          {ACTION_OWNER_ROLE_HELPER}
          {actionOwnerUnassigned ? ` ${ACTION_OWNER_ASSIGN_STEP_HINT}` : ''}
        </Typography>
        {actionOwnerUnassigned && onOpenAssignAction ? (
          <Button
            size="small"
            variant="text"
            onClick={onOpenAssignAction}
            data-testid="risk-decision-open-assign-action"
            sx={{ mt: 0.5, px: 0, fontWeight: 700 }}
          >
            Open Corrective action
          </Button>
        ) : null}
      </Box>
        </>
      ) : null}
      {understandRiskVariant ? (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, mb: 1, lineHeight: 1.5 }}
          data-testid="risk-decision-current-risk-owner"
        >
          {riskOwnerLabel}: {normalizedPatternOwner}
        </Typography>
      ) : null}
      {actionGuard ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {actionGuard}
        </Alert>
      ) : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1.25 }}>
        <Button
          variant="outlined"
          disabled={actionDisabled || groupPatternActionBusy}
          onClick={() => triggerAction('assign')}
          data-testid="risk-decision-update-risk-owner"
          sx={{ fontWeight: 800 }}
        >
          {assignRiskOwnerLabel}
        </Button>
        <Button
          variant="text"
          size="small"
          startIcon={<MoreHorizIcon />}
          aria-label="More actions"
          disabled={!group}
          onClick={(event) => setMoreActionsAnchor(event.currentTarget)}
          sx={{ fontWeight: 700, color: 'text.secondary' }}
        >
          More actions
        </Button>
        <Menu
          anchorEl={moreActionsAnchor}
          open={Boolean(moreActionsAnchor)}
          onClose={closeMoreActions}
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItem
            disabled={actionDisabled || groupPatternActionBusy}
            onClick={() => {
              triggerAction('suppress');
              closeMoreActions();
            }}
          >
            <ListItemText primary="Suppress 8 hours" secondary="Pause similar future observations" />
          </MenuItem>
          <MenuItem
            disabled={actionDisabled || groupPatternActionBusy}
            onClick={() => {
              triggerAction('close');
              closeMoreActions();
            }}
          >
            <ListItemText primary="Close recurring risk" secondary="Close open linked records in this window" />
          </MenuItem>
        </Menu>
      </Stack>
      {!understandRiskVariant ? (
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Grouping basis</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{groupingBasis}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Affected scope</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{scopeSummary || 'Not reported'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Policy / recurrence</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {[policyLabel, recurrenceLabel].filter(Boolean).join(' · ') || 'Not reported'}
          </Typography>
        </Grid>
      </Grid>
      ) : null}
    </Paper>
  );
};

type GroupedSummarySectionProps = {
  title: string;
  children: React.ReactNode;
};

const GroupedSummarySection: React.FC<GroupedSummarySectionProps> = ({ title, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      sx={{ fontSize: 10.5, letterSpacing: 0.85, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}
    >
      {title}
    </Typography>
    <Grid container spacing={1.25} sx={{ mt: 0.5 }}>
      {children}
    </Grid>
  </Box>
);

type GroupedSummaryFieldProps = {
  label: string;
  value: string;
  helper?: string;
  md?: number;
};

const GroupedSummaryField: React.FC<GroupedSummaryFieldProps> = ({ label, value, helper, md = 6 }) => (
  <Grid item xs={12} md={md}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {value || 'Not reported'}
    </Typography>
    {helper ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
        {helper}
      </Typography>
    ) : null}
  </Grid>
);

export type GroupedIncidentSummaryProps = {
  hideWorkflowStepper?: boolean;
  affectedEntity: string;
  zone: string;
  camera: string;
  device: string;
  policyLabel: string;
  relatedSummary: string;
  recurrenceLabel: string;
  whatToKnow: string;
  patternOwner: string;
  followUpOwner: string;
  followUpState: string;
  nextReviewSummary: string;
  workOrderSummary: string;
};

export const GroupedIncidentSummary: React.FC<GroupedIncidentSummaryProps> = ({
  hideWorkflowStepper = false,
  affectedEntity,
  zone,
  camera,
  device,
  policyLabel,
  relatedSummary,
  recurrenceLabel,
  whatToKnow,
  patternOwner,
  followUpOwner,
  followUpState,
  nextReviewSummary,
  workOrderSummary,
}) => {
  const workflowProgress = buildPatternReviewWorkflowProgress({
    patternOwnerLabel: patternOwner,
    followUpOwnerLabel: followUpOwner,
    reviewComplete: Boolean(followUpState) && !/^needs review$/i.test(String(followUpState || '').trim()),
    followUpMeasured: /measured|improved|closed|recorded/i.test(`${workOrderSummary} ${followUpState}`),
  });

  return (
  <Paper
    variant="outlined"
    sx={{
      p: 2.25,
      mb: 2,
      borderRadius: 3,
      borderColor: '#d0d7e2',
      bgcolor: '#fbfcfe',
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
    }}
  >
    <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
      Pattern context
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.55, mb: 1.25, lineHeight: 1.55 }}>
      {whatToKnow}
    </Typography>
    <GroupedSummarySection title="Status">
      {!hideWorkflowStepper ? (
        <Grid item xs={12}>
          <SafetyWorkflowStepper
            progress={workflowProgress}
            compact
            steps={GROUPED_SAFETY_REPORT_WORKFLOW_STEPS}
          />
        </Grid>
      ) : null}
      <GroupedSummaryField
        label="Risk pattern"
        value={[relatedSummary, recurrenceLabel].filter(Boolean).join(' · ') || 'Needs review'}
        md={12}
      />
      <GroupedSummaryField label="Follow-up status" value={followUpState || 'Not started'} md={6} />
    </GroupedSummarySection>
    <GroupedSummarySection title="Ownership">
      <GroupedSummaryField
        label={displaySafetyManagerLabel('pattern_owner')}
        value={ownerRoleIsUnassigned(patternOwner) ? 'Unassigned' : patternOwner}
        helper={RISK_OWNER_ROLE_HELPER}
      />
      <GroupedSummaryField
        label={displaySafetyManagerLabel('followup_owner')}
        value={ownerRoleIsUnassigned(followUpOwner) ? 'Unassigned' : followUpOwner}
        helper={
          ownerRoleIsUnassigned(followUpOwner)
            ? `${ACTION_OWNER_ROLE_HELPER} ${ACTION_OWNER_ASSIGN_STEP_HINT}`
            : ACTION_OWNER_ROLE_HELPER
        }
      />
    </GroupedSummarySection>
    <GroupedSummarySection title="Location">
      <GroupedSummaryField label="Affected entity" value={affectedEntity || 'Not identified'} />
      <GroupedSummaryField
        label="Zone / camera"
        value={[zone, camera].filter(Boolean).join(' · ') || 'Not reported'}
      />
      <GroupedSummaryField
        label="Device / policy"
        value={[device, policyLabel].filter(Boolean).join(' · ') || 'Not reported'}
        md={12}
      />
    </GroupedSummarySection>
    <GroupedSummarySection title="Follow-up">
      <GroupedSummaryField
        label="Next review"
        value={nextReviewSummary || 'No next review time set'}
      />
      <GroupedSummaryField
        label="Work order"
        value={workOrderSummary || 'No measured outcome yet'}
      />
    </GroupedSummarySection>
  </Paper>
  );
};

export type RepresentativeAuditScopeBannerProps = {
  relatedCount?: number;
  representativeIncidentId?: string;
};

export const RepresentativeAuditScopeBanner: React.FC<RepresentativeAuditScopeBannerProps> = ({
  relatedCount,
  representativeIncidentId,
}) => (
  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
    {representativeAuditScopeBanner(relatedCount, representativeIncidentId)}
  </Alert>
);

export type DeepReviewChecklistProps = {
  id?: string;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  phaseSummary: Array<{ label: string; value: string }>;
  scopeBanner?: React.ReactNode;
  children: React.ReactNode;
};

export const DeepReviewChecklist: React.FC<DeepReviewChecklistProps> = ({
  id,
  expanded,
  onChange,
  phaseSummary,
  scopeBanner,
  children,
}) => (
  <Accordion id={id} expanded={expanded} onChange={(_, nextExpanded) => onChange(nextExpanded)} disableGutters>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box>
        <Typography sx={{ fontWeight: 700 }}>{REPRESENTATIVE_AUDIT_LABEL}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.5 }}>
          {REPRESENTATIVE_AUDIT_SUBTITLE} ({DEEP_REVIEW_AUDIT_LABEL}: Risk pattern → Evidence review → Impact measurement → Report preparation —{' '}
          {phaseSummary.map((item) => `${item.label} ${item.value}`).join(' | ')})
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pb: 0 }}>
      {scopeBanner}
      {children}
    </AccordionDetails>
  </Accordion>
);

export type GroupedProgressInput = {
  workflowStatus: string;
  opsState: string;
  evidenceReady: boolean;
  followUpStarted: boolean;
};

export function deriveGroupedProgressSummary({
  workflowStatus,
  opsState,
  evidenceReady,
  followUpStarted,
}: GroupedProgressInput): Array<{ label: string; value: string }> {
  const workflow = String(workflowStatus || '').trim().toUpperCase();
  const ops = String(opsState || '').trim().toLowerCase();
  return [
    { label: 'Detected', value: '✓' },
    { label: 'Review', value: evidenceReady ? '✓' : 'pending' },
    {
      label: 'Corrective action',
      value:
        workflow === 'CLOSED' || workflow === 'SUPPRESSED'
          ? '✓'
          : workflow === 'IN_REVIEW' || ops === 'in_progress'
            ? 'in progress'
            : 'pending',
    },
    {
      label: 'Impact measurement',
      value:
        workflow === 'CLOSED'
          ? '✓'
          : followUpStarted
            ? 'in progress'
            : 'not started',
    },
    {
      label: 'Report preparation',
      value:
        workflow === 'CLOSED' || workflow === 'SUPPRESSED'
          ? '✓'
          : 'pending',
    },
  ];
}

export type GroupedWorkflowContainerProps = {
  groupedReviewMode: boolean;
  groupedDeepReviewOpen: boolean;
  setGroupedDeepReviewOpen: (expanded: boolean) => void;
  groupedProgressSummary: Array<{ label: string; value: string }>;
  relatedCount?: number;
  representativeIncidentId?: string;
  auditPreamble?: React.ReactNode;
  children: React.ReactNode;
};

export const GroupedWorkflowContainer: React.FC<GroupedWorkflowContainerProps> = ({
  groupedReviewMode,
  groupedDeepReviewOpen,
  setGroupedDeepReviewOpen,
  groupedProgressSummary,
  relatedCount,
  representativeIncidentId,
  auditPreamble,
  children,
}) => {
  if (!groupedReviewMode) return <>{children}</>;
  return (
    <DeepReviewChecklist
      id="representative-incident-audit"
      expanded={groupedDeepReviewOpen}
      onChange={setGroupedDeepReviewOpen}
      phaseSummary={groupedProgressSummary}
      scopeBanner={
        <RepresentativeAuditScopeBanner
          relatedCount={relatedCount}
          representativeIncidentId={representativeIncidentId}
        />
      }
    >
      <Box>
        {auditPreamble}
        {children}
      </Box>
    </DeepReviewChecklist>
  );
};

export type GroupedIncidentReviewWorkspaceProps = {
  title: string;
  workflowLabel: string;
  evidenceStatusLabel: string;
  patternVolumeCount: number;
  patternVolumeKind?: PatternVolumeKind;
  qualifyingEntryCount?: number | null;
  occupancyDurationMs?: number | null;
  forkliftCoPresenceEntryCount?: number | null;
  representativeIncidentId?: string;
  currentIncidentId?: string;
  recurrenceLabel?: string;
  firstSeenLabel?: string;
  lastSeenLabel?: string;
  seriesSummaryText?: string;
  whatToKnow: string;
  patternOwner: string;
  followUpOwner: string;
  onViewRelatedIncidents: () => void;
  nextStepLabel?: string;
  primaryLocationLabel?: string;
  reportReadinessLabel?: string;
  reportReadinessHelper?: string;
  evidenceReviewIsCurrent?: boolean;
  currentWorkflowStageLabel?: string;
  blockerSummary?: string;
  nextRequiredActionTitle?: string;
  nextRequiredActionBody?: string;
  nextRequiredActionSecondary?: string;
  nextRequiredActionButtonLabel?: string;
  nextRequiredActionHref?: string | null;
  onNextRequiredActionButton?: () => void;
  nextRequiredActionSecondaryButtonLabel?: string;
  onNextRequiredActionSecondaryButton?: () => void;
  representativeEventHelper?: string;
  reviewWindowContext: ReviewWindowContext;
  afterSignedScanTimeline?: RiskPatternWorkflowReviewTimelineProps | null;
  siteTimeZone?: string | null;
  /** Hide Epic 93 report-readiness as Pattern lifecycle. */
  containPatternReportLifecycle?: boolean;
};

export const GroupedIncidentReviewWorkspace: React.FC<GroupedIncidentReviewWorkspaceProps> = ({
  title,
  workflowLabel,
  evidenceStatusLabel,
  evidenceReviewIsCurrent = false,
  patternVolumeCount,
  patternVolumeKind: _patternVolumeKind = 'review_window',
  qualifyingEntryCount = null,
  occupancyDurationMs = null,
  forkliftCoPresenceEntryCount = null,
  representativeIncidentId,
  recurrenceLabel,
  firstSeenLabel,
  lastSeenLabel,
  seriesSummaryText: _seriesSummaryText,
  whatToKnow,
  onViewRelatedIncidents,
  nextStepLabel,
  primaryLocationLabel,
  reportReadinessLabel,
  reportReadinessHelper,
  currentWorkflowStageLabel,
  blockerSummary,
  nextRequiredActionTitle = 'Next required action',
  nextRequiredActionBody,
  nextRequiredActionSecondary,
  nextRequiredActionButtonLabel,
  nextRequiredActionHref,
  onNextRequiredActionButton,
  nextRequiredActionSecondaryButtonLabel,
  onNextRequiredActionSecondaryButton,
  representativeEventHelper,
  reviewWindowContext,
  afterSignedScanTimeline = null,
  siteTimeZone = null,
  containPatternReportLifecycle = false,
}) => {
  const reportChipLabel = (() => {
    const raw = String(reportReadinessLabel || '').trim();
    if (!raw || /^blocked$/i.test(raw)) return 'Pending required steps';
    return raw;
  })();
  const reportReady = /^ready/i.test(reportChipLabel);

  return (
  <Box data-testid="persistent-risk-summary-header" sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
      {RISK_REVIEW_LABEL}
    </Typography>
    <Typography sx={{ fontSize: 11, letterSpacing: 0.35, color: 'text.secondary', fontWeight: 700, mt: 0.35 }}>
      Risk pattern
    </Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 900, lineHeight: 1.15, mt: 0.15 }} data-testid="persistent-risk-title">
      {title}
    </Typography>
    <Typography
      sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800, mt: 0.85 }}
      data-testid="pattern-evaluation-window-label"
    >
      {PATTERN_EVALUATION_WINDOW_LABEL}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      data-testid="hotspot-operational-summary"
      sx={{ fontWeight: 700, lineHeight: 1.55, mt: 0.35 }}
    >
      {formatHotspotOperationalSummary({
        observations: patternVolumeCount,
        qualifyingEntries: qualifyingEntryCount,
        occupancyDurationMs,
        forkliftCoPresenceEntries: forkliftCoPresenceEntryCount,
      })}
    </Typography>

    <Paper
      variant="outlined"
      data-testid="grouped-next-required-action-card"
      sx={{
        p: 2,
        mt: 1.5,
        borderRadius: 3,
        borderWidth: 2,
        borderColor: 'primary.main',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.12)' : '#f0f7ff',
        boxShadow: '0 10px 28px rgba(25, 118, 210, 0.12)',
      }}
    >
      <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'primary.main', textTransform: 'uppercase', fontWeight: 800 }}>
        {nextRequiredActionTitle}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5, lineHeight: 1.35 }} data-testid="persistent-risk-next-step">
        {nextRequiredActionBody || nextStepLabel || 'Review this recurring risk pattern'}
      </Typography>
      {nextRequiredActionSecondary ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
          {nextRequiredActionSecondary}
        </Typography>
      ) : null}
      {(nextRequiredActionButtonLabel && onNextRequiredActionButton)
        || (nextRequiredActionSecondaryButtonLabel && onNextRequiredActionSecondaryButton) ? (
        <Box sx={{ mt: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {nextRequiredActionButtonLabel && onNextRequiredActionButton ? (
            <Button
              size="small"
              variant="contained"
              href={nextRequiredActionHref || undefined}
              onClick={(event) => {
                if (nextRequiredActionHref) event.preventDefault();
                onNextRequiredActionButton();
              }}
              data-testid="grouped-next-required-action-button"
            >
              {nextRequiredActionButtonLabel}
            </Button>
          ) : null}
          {nextRequiredActionSecondaryButtonLabel && onNextRequiredActionSecondaryButton ? (
            <Button
              size="small"
              variant="outlined"
              onClick={onNextRequiredActionSecondaryButton}
              data-testid="grouped-next-required-action-secondary-button"
            >
              {nextRequiredActionSecondaryButtonLabel}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Paper>

    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        mt: 1.25,
        borderRadius: 3,
        borderColor: '#d0d7e2',
        bgcolor: '#fbfcfe',
      }}
    >
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" color="text.secondary">Status</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }} data-testid="persistent-risk-status">
            {displayPatternWorkflowLabel(workflowLabel) || 'Needs review'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" color="text.secondary">Current workflow stage</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main' }} data-testid="persistent-risk-workflow-stage">
            {currentWorkflowStageLabel || 'Risk pattern'}
          </Typography>
        </Grid>
        {!containPatternReportLifecycle ? (
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" color="text.secondary">Draft report readiness</Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 800, color: reportReady ? 'success.main' : 'warning.main' }}
            data-testid="persistent-risk-report-readiness"
          >
            {reportChipLabel}
          </Typography>
        </Grid>
        ) : null}
        {blockerSummary ? (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Blocker</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: 'warning.dark' }} data-testid="persistent-risk-blocker">
              {blockerSummary}
            </Typography>
          </Grid>
        ) : null}
      </Grid>
    </Paper>

    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
      <Chip
        label={buildGroupedEvidenceStatusChipLabel({
          evidenceReviewIsCurrent,
          evidenceStatusLabel,
        })}
        variant="outlined"
        color={evidenceReviewIsCurrent ? 'primary' : 'default'}
        size="small"
      />
      {!containPatternReportLifecycle ? (
      <Chip
        label={`Report preparation: ${reportChipLabel}`}
        color={reportReady ? 'success' : 'warning'}
        variant={reportReady ? 'filled' : 'outlined'}
        size="small"
        sx={{ fontWeight: 800 }}
      />
      ) : null}
    </Stack>

    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        mt: 1.25,
        borderRadius: 3,
        borderColor: '#e2e8f0',
        bgcolor: '#ffffff',
      }}
    >
      {afterSignedScanTimeline ? (
        <Box sx={{ mb: 1.5 }} data-testid="grouped-review-timeline-selector">
          <AfterSignedScanTimelineSelector
            signedScanAt={afterSignedScanTimeline.signedScanAt}
            timelineMode={afterSignedScanTimeline.timelineMode}
            windowStartIso={afterSignedScanTimeline.windowStartIso}
            windowEndIso={afterSignedScanTimeline.windowEndIso}
            onTimelineChange={afterSignedScanTimeline.onTimelineChange}
            onResolveStartBeforeSignedScan={afterSignedScanTimeline.onResolveStartBeforeSignedScan}
            siteTimeZone={siteTimeZone}
          />
        </Box>
      ) : null}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">
            {formatReviewWindowHeaderCaption(reviewWindowContext)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }} data-testid="grouped-review-window-summary">
            {formatReviewWindowHeaderValue(reviewWindowContext, siteTimeZone)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Primary location</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {primaryLocationLabel || recurrenceLabel || 'Not reported'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Representative evidence</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }} data-testid="persistent-risk-representative-event">
            {representativeIncidentId || lastSeenLabel || 'Not reported'}
          </Typography>
          {representativeEventHelper ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.45 }}>
              {representativeEventHelper}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Latest observation</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {lastSeenLabel || firstSeenLabel || 'Not reported'}
          </Typography>
        </Grid>
        {reportReadinessHelper ? (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Report preparation note</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {reportReadinessHelper}
            </Typography>
          </Grid>
        ) : null}
      </Grid>
    </Paper>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55 }}>
      {whatToKnow}
    </Typography>
    {onViewRelatedIncidents ? (
      <Button
        variant="text"
        size="small"
        onClick={onViewRelatedIncidents}
        sx={{ mt: 0.75, px: 0, minWidth: 0, fontWeight: 800, textTransform: 'none' }}
      >
        View pattern observations
      </Button>
    ) : null}
  </Box>
  );
};

export type IncidentDiagnosticsAccordionProps = {
  title: string;
  subtitle: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
};

export const IncidentDiagnosticsAccordion: React.FC<IncidentDiagnosticsAccordionProps> = ({
  title,
  subtitle,
  defaultExpanded = false,
  children,
}) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {subtitle}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pb: 0 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);
